import React, { useState } from 'react';
import { Trash2Icon } from 'lucide-react';
import type { Granularity, Milestone } from '../types/life';
import { cn } from '../utils/cn';
import { formatCountdown, formatElapsed } from '../utils/format';
import { milestoneStart, milestoneUnitLabel } from '../utils/lifeGrid';
import { useNow } from '../hooks/useNow';
import { Modal } from './Modal';
export interface MilestoneDraft {
  id?: string;
  title: string;
  granularity: Granularity;
  date: string;
}
interface MilestoneDialogProps {
  open: boolean;
  draft: MilestoneDraft | null;
  onClose: () => void;
  onSave: (milestone: Milestone) => void;
  onDelete: (id: string) => void;
}
const LEVELS: Array<{
  id: Granularity;
  label: string;
}> = [{
  id: 'days',
  label: 'День'
}, {
  id: 'weeks',
  label: 'Неделя'
}, {
  id: 'months',
  label: 'Месяц'
}, {
  id: 'years',
  label: 'Год'
}];
const inputClass = 'w-full border border-line bg-paper px-3 py-2.5 text-sm text-ink outline-none transition-colors duration-150 ease-swift focus:border-accent';
function newId(): string {
  return `ms-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}
function MilestoneForm({
  draft,
  onClose,
  onSave,
  onDelete
}: Omit<MilestoneDialogProps, 'open'> & {
  draft: MilestoneDraft;
}) {
  const now = useNow(1000);
  const [title, setTitle] = useState(draft.title);
  const [granularity, setGranularity] = useState<Granularity>(draft.granularity);
  const [date, setDate] = useState(draft.date);
  const candidate: Milestone = {
    id: draft.id ?? 'preview',
    title,
    granularity,
    date
  };
  const start = milestoneStart(candidate);
  const ahead = start.getTime() > now.getTime();
  const valid = Boolean(date) && title.trim().length > 0;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    onSave({
      id: draft.id ?? newId(),
      title: title.trim(),
      granularity,
      date
    });
  };
  return <form onSubmit={submit} className="space-y-6" data-id="element-15" data-mp="2bp3t4ri19gri">
      <div className="space-y-2" data-id="element-16" data-mp="2bnizd43jm1up">
        <label htmlFor="ms-title" className="text-muted block text-xs font-medium" data-id="element-17" data-mp="2bmr3ds61a23w">
          Что произойдёт
        </label>
        <input id="ms-title" autoFocus value={title} onChange={e => setTitle(e.target.value)} placeholder="Например: защита диплома" className={inputClass} required data-id="element-18" data-mp="1znc9bzzvyat4" />
      </div>

      <div className="space-y-2" data-id="element-19" data-mp="28h7fnbcqu7ac">
        <span className="text-muted block text-xs font-medium" data-id="element-20" data-mp="1kpa7pvcezzei">Уровень</span>
        <div className="border-line flex border p-1" role="group" aria-label="Уровень майлстоуна" data-id="element-21" data-mp="1krd2esoiigpk">
          {LEVELS.map(level => {
          const active = level.id === granularity;
          return <button key={level.id} type="button" aria-pressed={active} onClick={() => setGranularity(level.id)} className={cn('flex-1 px-3 py-1.5 text-xs font-medium transition-colors duration-150 ease-swift', active ? 'bg-ink text-paper' : 'text-muted hover:text-ink')} data-id="element-22" data-mp="1pomtsdvgp2g1">
                {level.label}
              </button>;
        })}
        </div>
      </div>

      <div className="space-y-2" data-id="element-23" data-mp="dw4adcr7zrp9">
        <label htmlFor="ms-date" className="text-muted block text-xs font-medium" data-id="element-24" data-mp="dx6yjcx5rg1h">
          Дата внутри этой единицы
        </label>
        <input id="ms-date" type="date" value={date} onChange={e => setDate(e.target.value)} className={cn(inputClass, 'font-mono')} required data-id="element-25" data-mp="16k001xidgjcj" />
      </div>

      {date && <div className="border-line flex items-baseline justify-between gap-4 border-t pt-4" data-id="element-26" data-mp="wtf5cl5tjyyw">
          <span className="text-muted text-xs" data-id="element-27" data-mp="11s9pprjfpvl4">{milestoneUnitLabel(candidate)}</span>
          <span className={cn('font-mono text-xs tabular-nums', ahead ? 'text-accent' : 'text-muted')} data-id="element-28" data-mp="1pim1xelpqehi">
            {ahead ? formatCountdown(start, now) : `прошло ${formatElapsed(start, now)}`}
          </span>
        </div>}

      <div className="flex items-center justify-between gap-3" data-id="element-29" data-mp="5pch8o6iebfz">
        <div className="flex items-center gap-3" data-id="element-30" data-mp="of26kgspxip">
          <button type="submit" disabled={!valid} className="bg-ink text-paper hover:bg-accent disabled:bg-line disabled:text-muted px-5 py-2.5 text-sm font-semibold transition-colors duration-150 ease-swift disabled:cursor-not-allowed" data-id="element-31" data-mp="qsxmufe1bqs">
            Сохранить
          </button>
          <button type="button" onClick={onClose} className="text-muted hover:text-ink px-2 text-sm transition-colors duration-150 ease-swift" data-id="element-32" data-mp="kcxmooquwmr2">
            Отмена
          </button>
        </div>
        {draft.id && <button type="button" onClick={() => onDelete(draft.id as string)} className="text-muted hover:text-ink transition-colors duration-150 ease-swift" aria-label="Удалить майлстоун" data-id="element-33" data-mp="akrw9jg96ddz">
            <Trash2Icon className="h-4 w-4" data-id="element-34" data-mp="1anhjuq9ski3s" />
          </button>}
      </div>
    </form>;
}
export function MilestoneDialog({
  open,
  draft,
  onClose,
  onSave,
  onDelete
}: MilestoneDialogProps) {
  return <Modal open={open && Boolean(draft)} title={draft?.id ? 'Майлстоун' : 'Новый майлстоун'} onClose={onClose} data-id="element-35" data-mp="1kjswxih7hbza">
      {draft && <MilestoneForm key={draft.id ?? `${draft.granularity}-${draft.date}`} draft={draft} onClose={onClose} onSave={onSave} onDelete={onDelete} data-id="element-36" data-mp="1ons95ovgdomu" />}
    </Modal>;
}